import { useEffect, useState } from 'react';
import AnimatedCounter from '@components/common/AnimatedCounter';
import { FiGlobe, FiUsers, FiAward, FiClock, FiHeart, FiBook } from 'react-icons/fi';

const ImpactMetricsSection = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger animation when component mounts
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const metrics = [
    {
      value: 2,
      suffix: '+',
      label: 'Countries Reached',
      description: 'Through mentoring inmates, local schools, and orphanage',
      icon: FiGlobe,
      color: 'var(--color-accent-6)',
    },
    {
      value: 20,
      suffix: '+',
      label: 'Inmates Taught',
      description: 'In General Santos Jail and San Fernando Jail in Philippines',
      icon: FiUsers,
      color: 'var(--color-accent-5)',
    },
    {
      value: 100,
      suffix: '+',
      label: 'Community Workshops',
      description: 'Educational sessions in Philippines, India and beyond',
      icon: FiBook,
      color: 'var(--color-accent-7)',
    },
    {
      value: 2,
      label: 'Major Tournaments',
      description: 'Organized and fundraised for national championships',
      icon: FiAward,
      color: 'var(--color-accent-4)',
    },
    {
      value: 500,
      suffix: '+',
      label: 'Lives Impacted',
      description: 'Individuals reached through chess and community service',
      icon: FiHeart,
      color: 'var(--color-accent-6)',
    },
    {
      value: 4,
      suffix: '+',
      label: 'Years of Service',
      description: 'Dedicated to using chess for social transformation',
      icon: FiClock,
      color: 'var(--color-accent-5)',
    },
  ];

  return (
    <section className="py-16 md:py-20">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ color: 'var(--color-heading)' }}
          >
            Impact by the Numbers
          </h2>
          <p
            className="text-lg md:text-xl leading-relaxed"
            style={{ color: 'var(--color-subheading)' }}
          >
            Measurable change through dedication, compassion, and the power of chess
          </p>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {metrics.map((metric, index) => (
            <div
              key={index}
              className="p-8 rounded-lg transition-all duration-300"
              style={{
                backgroundColor: 'var(--color-card-bg)',
                borderColor: 'var(--color-card-border)',
                borderWidth: '1px',
                borderStyle: 'solid',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = metric.color;
                e.currentTarget.style.transform = 'translateY(-8px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--color-card-border)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {/* Icon */}
              <div
                className="mb-4 w-16 h-16 rounded-full flex items-center justify-center"
                style={{
                  backgroundColor: 'var(--color-accent-1)',
                }}
              >
                {metric.icon && <metric.icon size={32} style={{ color: metric.color }} />}
              </div>

              {/* Counter */}
              <div className="mb-2">
                {isVisible ? (
                  <span
                    className="text-4xl md:text-5xl font-bold"
                    style={{ color: metric.color }}
                  >
                    <AnimatedCounter
                      end={metric.value}
                      duration={2000}
                      suffix={metric.suffix || ''}
                    />
                  </span>
                ) : (
                  <span
                    className="text-4xl md:text-5xl font-bold"
                    style={{ color: metric.color }}
                  >
                    0{metric.suffix || ''}
                  </span>
                )}
              </div>

              {/* Label */}
              <h3
                className="text-xl font-semibold mb-2"
                style={{ color: 'var(--color-heading)' }}
              >
                {metric.label}
              </h3>

              {/* Description */}
              <p
                className="text-sm leading-relaxed"
                style={{ color: 'var(--color-muted)' }}
              >
                {metric.description}
              </p>
            </div>
          ))}
        </div>

        {/* Additional Impact Stats */}
        <div className="mt-16 max-w-5xl mx-auto">
          <div
            className="p-8 md:p-12 rounded-lg"
            style={{
              backgroundColor: 'var(--color-accent-1)',
              borderColor: 'var(--color-accent-3)',
              borderWidth: '1px',
              borderStyle: 'solid',
            }}
          >
            <h3
              className="text-2xl md:text-3xl font-bold mb-8 text-center"
              style={{ color: 'var(--color-heading)' }}
            >
              Beyond the Statistics
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div
                  className="text-5xl font-bold mb-2"
                  style={{ color: 'var(--color-accent-7)' }}
                >
                  ∞
                </div>
                <div
                  className="text-lg font-semibold mb-2"
                  style={{ color: 'var(--color-heading)' }}
                >
                  Ripple Effect
                </div>
                <p
                  className="text-sm"
                  style={{ color: 'var(--color-subheading)' }}
                >
                  Each person taught goes on to inspire others, creating unlimited potential for change
                </p>
              </div>

              <div className="text-center">
                <div
                  className="text-5xl font-bold mb-2"
                  style={{ color: 'var(--color-accent-7)' }}
                >
                  100%
                </div>
                <div
                  className="text-lg font-semibold mb-2"
                  style={{ color: 'var(--color-heading)' }}
                >
                  Commitment
                </div>
                <p
                  className="text-sm"
                  style={{ color: 'var(--color-subheading)' }}
                >
                  Every initiative driven by genuine desire to make a difference, not just numbers
                </p>
              </div>

              <div className="text-center">
                <div
                  className="text-5xl font-bold mb-2"
                  style={{ color: 'var(--color-accent-7)' }}
                >
                  0
                </div>
                <div
                  className="text-lg font-semibold mb-2"
                  style={{ color: 'var(--color-heading)' }}
                >
                  Cost to Communities
                </div>
                <p
                  className="text-sm"
                  style={{ color: 'var(--color-subheading)' }}
                >
                  All workshops and initiatives provided free of charge to underserved communities
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Quote */}
        <div className="mt-12 max-w-3xl mx-auto text-center">
          <blockquote
            className="text-xl md:text-2xl font-medium italic leading-relaxed"
            style={{ color: 'var(--color-heading)' }}
          >
            "True impact isn't measured in numbers alone, but in the hope restored, skills learned, and lives transformed—one move, one person, one community at a time."
          </blockquote>
        </div>
      </div>
    </section>
  );
};

export default ImpactMetricsSection;
